import { type FunctionComponent, useEffect, useState } from 'react';

import { Stage, type StageStateT } from './Stage';

import './style.scss';

type PropsT = {
  current: number;
  stages: string[];
};

const TRANSITION_MS = 550;

type DisplayT = {
  index: number;
  leaving: boolean;
  state: StageStateT;
};

type TargetT = {
  index: number;
  state: StageStateT;
};

export const BuildStages: FunctionComponent<PropsT> = ({ current, stages }) => {
  const labels = ['Initializing your experience build', ...stages];
  const lastIndex = labels.length - 1;

  // BEWARE: Mind, `current` is set to Number.MAX_SAFE_INTEGER once the whole
  // build is completed, in which case we keep displaying the last stage,
  // marked as completed.
  const isDone = current > lastIndex;
  const targetIndex = Math.min(current, lastIndex);
  const targetState: StageStateT = isDone ? 'COMPLETED' : 'ACTIVE';

  const [display, setDisplay] = useState<DisplayT>({
    index: targetIndex,
    leaving: false,
    state: targetState,
  });

  // Whenever the target stage changes, first mark the currently displayed
  // stage as completed (to play its checkmark animation), then, after a
  // short delay, switch over to the next (or final) stage. This adjusts
  // state during render (rather than in an effect) in response to a prop
  // change, per https://react.dev/learn/you-might-not-need-an-effect.
  const [prevTarget, setPrevTarget] = useState<TargetT>({
    index: targetIndex,
    state: targetState,
  });
  if (prevTarget.index !== targetIndex || prevTarget.state !== targetState) {
    setPrevTarget({ index: targetIndex, state: targetState });
    setDisplay((prev) => ({ ...prev, leaving: true, state: 'COMPLETED' }));
  }

  useEffect(() => {
    if (!display.leaving) return undefined;
    const id = setTimeout(() => {
      setDisplay({ index: targetIndex, leaving: false, state: targetState });
    }, TRANSITION_MS);
    return () => {
      clearTimeout(id);
    };
  }, [display.leaving, targetIndex, targetState]);

  return (
    <div styleName="container">
      <div
        key={display.index}
        styleName={display.leaving ? 'leaving' : 'entering'}
      >
        <Stage label={labels[display.index] ?? ''} state={display.state} />
      </div>
    </div>
  );
};
